<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { Plus, Upload, AlertTriangle } from 'lucide-vue-next';
// shadcn-vue 컴포넌트 임포트
import { Button } from '@/components/shadcnui/button';
import { Label } from '@/components/shadcnui/label';
import { Switch } from '@/components/shadcnui/switch';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Spinner } from '@/components/shadcnui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/shadcnui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcnui/select';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationPrevious, PaginationNext } from '@/components/shadcnui/pagination';
// composable 및 store 임포트
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useChipset } from '@/composable/useChipset';
import { usePkgtype } from '@/composable/usePkgtype';
import { usePkg } from '@/composable/usePkg';
import { useApp } from '@/composable/useApp';
import { useApprs } from '@/composable/useApprs';
import { useJSONEditor } from '@/composable/useJSONEditor';
// 타입 임포트
import type { app, apkUpload, updateshowapp, updatenoteapp, release } from '../types/types';
// JSON 에디터 임포트
import JsonEditorVue from 'json-editor-vue';

// 스토어 및 composable 초기화
const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadChipsetList } = useChipset();
const { onReadPkgtypeList } = usePkgtype();
const { onReadPkgList } = usePkg();
const { onReadAppList, onUpdateShowApp, onUpdatenoteApp, onDeleteApp, onApkUpload, onPaginationApp } = useApp();
const { onInUseApp } = useApprs();
const { handleRenderMenu, handleContextMenu } = useJSONEditor();

// 상태 관리
const isDialogOpen = ref(false);
const isUpdateMode = ref(false);
const selectedApp = ref<any>(null);
const selectedFile = ref<File | null>(null);
const currentPage = ref(1);
const totalpage = ref(1);
const filterChipset = ref(0);
const filterPackageType = ref(0);
const isDragging = ref(false);
const isLoading = ref(true);
const isSpinner = ref(false);
const ApkUploadError = ref("");
const isApkErrorDialogOpen = ref(false);
const isDeleteDialogOpen = ref(false);
const settingMode = ref(1);
const fileInputRef = ref<HTMLInputElement | null>(null);
const jsonData = ref({
  title: "",
  date: "",
  notes: [{
    title: "",
    info: [""]
  }]
});

const inuseReleaseList = ref<release[]>([]);

// 마우스 위치 및 툴팁 상태
const mousePos = ref({ x: 0, y: 0 });
const hoveredRelNote = ref<any>("");

watch([hoveredRelNote], () => {
  if (hoveredRelNote.value === null) {
    return
  }
  jsonData.value = JSON.parse(atob(hoveredRelNote.value));
});

// 마우스 이동 핸들러 (툴팁 위치 업데이트)
const handleMouseMove = (event: MouseEvent) => {
  mousePos.value = { x: event.clientX, y: event.clientY };
};

// 필터링 조건 변경 시 모델 리스트 업데이트
watch([filterPackageType, filterChipset], async () => {
  if (!settingMode.value) {
    currentPage.value = 1; // 페이지네이션 초기화
    UserStore.appfilter.chipset_id = filterChipset.value;
    UserStore.appfilter.package_id = filterPackageType.value;
    UserStore.appfilter.page = currentPage.value;

    totalpage.value = await onPaginationApp(UserStore.appfilter) ?? 1;
    await onReadAppList(UserStore.appfilter);
  }
});

// 다이얼로그 핸들러
const openUploadDialog = () => {
  selectedApp.value = null;
  selectedFile.value = null;
  jsonData.value = {
    title: "",
    date: "",
    notes: [{
      title: "",
      info: [""]
    }]
  };
  isDialogOpen.value = true;

};
const openUpdateDialog = (app: app) => {
  if (!UserStore.appEdit) {
    return;
  }
  isUpdateMode.value = true;
  selectedApp.value = app;
  if (app.relnote) {
    jsonData.value = JSON.parse(atob(app.relnote));
  }
};

// 파일 핸들러
const handleFileSelect = () => {
  fileInputRef.value?.click();
}
const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (target.files && target.files[0]) selectedFile.value = target.files[0];
  console.log(selectedFile);
};
const handleDrop = (e: DragEvent) => {
  const files = e.dataTransfer?.files;
  if (files && files.length > 0 && files[0].name.endsWith('.apk')) {
    selectedFile.value = files[0];
  }
};

// APK upload API 호출 핸들러
const handleUpload = async () => {
  if (!selectedFile.value) return alert("APK 파일을 선택해주세요");
  const apkUpload: apkUpload = {
    apkfile: selectedFile.value,
    relnote: btoa(JSON.stringify(jsonData.value)),
  }
  isSpinner.value = true;
  UserStore.appfilter.page = 1; // 페이지네이션을 첫 페이지로 리셋
  const uploadResult = await onApkUpload(apkUpload);
  if (uploadResult !== "Success") {
    ApkUploadError.value = uploadResult;
    isApkErrorDialogOpen.value = true;
  }
  console.log("업로드 결과:", uploadResult);
  await onReadAppList(UserStore.appfilter);
  isSpinner.value = false;
  isDialogOpen.value = false;
};

// 앱 Show Update API 호출 핸들러
const handleToggleShow = async (appId: number, checked: boolean) => {
  const updateShowApp: updateshowapp = { id: appId, isshow: checked ? 1 : 0 };
  await onUpdateShowApp(updateShowApp);
  await onReadAppList(UserStore.appfilter);
};
// 앱 Release Note Update API 호출 핸들러
const handleUpdateNote = async (appId: number) => {
  const updateNoteApp: updatenoteapp = { id: appId, relnote: btoa(JSON.stringify(jsonData.value)) };
  await onUpdatenoteApp(updateNoteApp);
  await onReadAppList(UserStore.appfilter);
  isUpdateMode.value = false
  currentPage.value = 1;
};

// 앱 Delete API 호출 핸들러
const handleDelete = async () => {
  console.log("삭제할 앱 ID:", selectedApp.value.id);
  isSpinner.value = true;
  await onDeleteApp(selectedApp.value.id);
  await onReadAppList(UserStore.appfilter);
  isSpinner.value = false;
  isUpdateMode.value = false;
  isDeleteDialogOpen.value = false;
  currentPage.value = 1;
};

// 앱 삭제 다이얼로그 오픈 핸들러 (사용 중인 릴리즈 체크)
const openDeleteDialog = async () => {
  inuseReleaseList.value = await onInUseApp(selectedApp.value.id);
  isDeleteDialogOpen.value = true;
};
// 앱 삭제 다이얼로그 클로즈 핸들러 (상태 초기화)
const closeDeleteDialog = () => {
  inuseReleaseList.value = [];
  isDeleteDialogOpen.value = false;
};

const paginationHandler = async (page: number) => {
  if (page < 1) {
    page = 1;
  } else if (page > totalpage.value) {
    page = totalpage.value;
  }
  currentPage.value = page;
  UserStore.appfilter.page = page;

  await onReadAppList(UserStore.appfilter);
};

// JSON 에디터 변경 핸들러
const onJsonChange = (value: any) => {
  console.log('변경된 데이터:', value);
  jsonData.value = value;
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(async () => {
  // 페이지네이션 및 필터링 상태
  if (UserStore.appfilter.chipset_id || UserStore.appfilter.package_id) {
    filterChipset.value = UserStore.appfilter.chipset_id;
    filterPackageType.value = UserStore.appfilter.package_id;
  }
  // 페이지네이션 상태 
  if (UserStore.appfilter.page) {
    currentPage.value = UserStore.appfilter.page;
  }
  await onReadChipsetList({ page: 0, size: 0 });
  await onReadPkgtypeList({ page: 0, size: 0 });
  await onReadPkgList({ chipset_id: 0, pkgtype_id: 0, page: 0, size: 0 });
  totalpage.value = await onPaginationApp(UserStore.appfilter) ?? 1;
  await onReadAppList(UserStore.appfilter).finally(() => {
    isLoading.value = false;
  });
  settingMode.value = 0;
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-2">
      <h1 class="text-3xl font-bold">앱</h1>
      <!-- 필터 -->
      <div class="flex gap-4 mb-2">
        <div class="w-48">
          <Label class="mb-2 block">Chipset</Label>
          <Select v-model="filterChipset">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="chipset in RSStore.chipsetList" :key="chipset.id" :value="chipset.id">
                {{ chipset.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="w-48">
          <Label class="mb-2 block">Package Type</Label>
          <Select v-model="filterPackageType">
            <SelectTrigger>
              <SelectValue placeholder="전체" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="0">전체</SelectItem>
              <SelectItem v-for="pkgtype in RSStore.pkgtypeList" :key="pkgtype.id" :value="pkgtype.id">
                {{ pkgtype.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="flex items-end">
          <Button @click="openUploadDialog" :disabled="!UserStore.appAdd">
            <Plus class="mr-2 h-4 w-4" /> Upload
          </Button>
        </div>
      </div>
    </div>
    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50">
            <TableHead class="px-5">ID</TableHead>
            <TableHead>Chipset</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Package Name</TableHead>
            <TableHead>Version Name</TableHead>
            <TableHead>Version Code</TableHead>
            <TableHead>Upload Date</TableHead>
            <TableHead>DL Count</TableHead>
            <TableHead class="px-5">Size</TableHead>
            <TableHead>Show</TableHead>
            <TableHead>Download</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태  -->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="12" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <template v-else>
            <TableRow v-for="app in RSStore.appList" :key="app.id" class="cursor-pointer hover:bg-accent/50"
              @click="openUpdateDialog(app)" @mousemove="handleMouseMove" @mouseleave="hoveredRelNote = null"
              @mouseenter="hoveredRelNote = app.relnote || 'No release notes.'">
              <TableCell class="px-5">{{ app.id }}</TableCell>
              <TableCell>{{RSStore.chipsetList.find(c => c.id === RSStore.pkgList.find(p => p.id ===
                app.package_id)?.chipset_id)?.name}}</TableCell>
              <TableCell>{{RSStore.pkgtypeList.find(pt => pt.id === RSStore.pkgList.find(p => p.id ===
                app.package_id)?.pkgtype_id)?.name}}</TableCell>
              <TableCell class="max-w-50 truncate">{{RSStore.pkgList.find(p => p.id === app.package_id)?.pkgname}}</TableCell>
              <TableCell>{{ app.vername }}</TableCell>
              <TableCell>{{ app.vercode }}</TableCell>
              <TableCell>{{ new Date(app.addeddate).toLocaleDateString('ko-KR', {
                year: 'numeric', month: '2-digit',
                day:
                  '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
              }) }}</TableCell>
              <TableCell>{{ app.dlcount }}</TableCell>
              <TableCell>{{ (app.size / (1024 * 1024)).toFixed(2) + ' MB' }}</TableCell>
              <TableCell @click.stop>
                <Switch :model-value="Boolean(app.isshow)" @update:model-value="handleToggleShow(app.id, $event)"
                  :disabled="!UserStore.appEdit" />
              </TableCell>
              <TableCell class="px-6">
                <a :href="app.link" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline"
                  @click.stop>
                  Link
                </a>
              </TableCell>
            </TableRow>
            <TableRow v-for="i in (UserStore.itemPerPage - RSStore.appList.length)" :key="'empty-' + i">
              <TableCell colspan="12">&nbsp;</TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>
    <!-- 페이지네이션 -->
    <div class="mt-4 flex justify-center">
      <Pagination v-model:page="currentPage" :total="totalpage * UserStore.itemPerPage"
        :items-per-page="UserStore.itemPerPage" :sibling-count="UserStore.siblingCount" show-edges>
        <PaginationContent v-slot="{ items }" class="flex items-center gap-1">
          <PaginationPrevious @click="paginationHandler(currentPage - RSStore.nextunit)" />
          <template v-for="(item, index) in items">
            <PaginationItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="paginationHandler(item.value)">
                {{ item.value }}
              </Button>
            </PaginationItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>
          <PaginationNext @click="paginationHandler(currentPage + RSStore.nextunit)" />
        </PaginationContent>
      </Pagination>
      <!-- 릴리즈 노트 팝업 -->
      <Teleport to="body">
        <div v-if="hoveredRelNote" :style="{
          position: 'fixed',
          left: `${mousePos.x + 5}px`,
          top: `${mousePos.y + 5}px`
        }"
          class="z-50 pointer-events-none min-w-80 w-auto bg-popover text-popover-foreground rounded-md border p-4 shadow-md outline-none">
          <div class="space-y-1">
            <h4 class="text-sm font-semibold">Release Note</h4>
            <div class="w-150">
              <JsonEditorVue class="h-100" v-model="jsonData" :mainMenuBar="false" :navigation-bar="false"
                :onRenderMenu="handleRenderMenu" :onContextMenu="handleContextMenu" :readOnly="false" />
            </div>
          </div>
        </div>
      </Teleport>
    </div>

    <!-- APK 업로드 다이얼로그 -->
    <Dialog v-model:open="isDialogOpen">
      <DialogContent class="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>앱 업로드</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div class="py-4 space-y-6">
          <div>
            <div class="flex items-center justify-between mb-2">
              <Label>APK File</Label>
              <Button variant="outline" size="sm" @click="handleFileSelect">파일 선택</Button>
            </div>
            <div class="border-2 border-dashed rounded-lg p-8 text-center transition-all"
              :class="isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'"
              @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
              <div v-if="selectedFile" class="space-y-2">
                <template v-if="!isSpinner">
                  <Upload class="mx-auto h-8 w-8" />
                </template>
                <template v-else>
                  <Spinner class="mx-auto h-8 w-8" />
                </template>
                <p class="text-sm font-medium">{{ selectedFile.name }}</p>
              </div>
              <div v-else class="space-y-2 text-muted-foreground">
                <Upload class="mx-auto h-8 w-8" />
                <p class="text-sm">APK 파일을 드래그하거나 버튼을 클릭하세요</p>
              </div>
            </div>
            <input ref="fileInputRef" type="file" accept=".apk" class="hidden" @change="handleFileChange" />
          </div>
          <div>
            <Label class="mb-2 block">Release Note</Label>
            <JsonEditorVue class="h-100" v-model="jsonData" :mainMenuBar="true" :navigation-bar="false"
              :onRenderMenu="handleRenderMenu" :onContextMenu="handleContextMenu" @update:modelValue="onJsonChange" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="isDialogOpen = false">Cancel</Button>
          <Button @click="handleUpload" :disabled="isSpinner || !selectedFile">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- 앱 상세/수정 다이얼로그 -->
    <Dialog v-model:open="isUpdateMode">
      <DialogContent class="sm:max-w-[1000px] h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>앱 상세</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div v-if="selectedApp" class="py-4 space-y-4 text-foreground " @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <Label class="text-sm text-muted-foreground">Chipset</Label>
              <p class="text-sm font-medium">{{RSStore.chipsetList.find(c => c.id === RSStore.pkgList.find(p => p.id
                ===
                selectedApp.package_id)?.chipset_id)?.name}}</p>
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">Type</Label>
              <p class="text-sm font-medium">{{RSStore.pkgtypeList.find(pt => pt.id === RSStore.pkgList.find(p => p.id ===
                selectedApp.package_id)?.pkgtype_id)?.name}}
              </p>
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">Package Name</Label>
              <p class="text-sm font-medium break-all">{{RSStore.pkgList.find(p => p.id ===
                selectedApp.package_id)?.pkgname}}</p>
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">Version Name</Label>
              <p class="text-sm font-medium">{{ selectedApp.vername }}</p>
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">Version Code</Label>
              <p class="text-sm font-medium">{{ selectedApp.vercode }}</p>
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">Upload Date</Label>
              <p class="text-sm font-medium">{{ selectedApp.addeddate }}</p>
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">DL Count</Label>
              <p class="text-sm font-medium">{{ selectedApp.dlcount }}</p>
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">File Size</Label>
              <p class="text-sm font-medium">{{ (selectedApp.size / (1024 * 1024)).toFixed(2) + ' MB' }}</p>
            </div>
          </div>
          <div class="space-y-2">
            <Label class="block">Release Note</Label>
            <JsonEditorVue class="h-100" v-model="jsonData" :mainMenuBar="true" :navigation-bar="false"
              :onRenderMenu="handleRenderMenu" :onContextMenu="handleContextMenu" @update:modelValue="onJsonChange" />
          </div>
        </div>
        <DialogFooter class="gap-2">
          <Button variant="outline" @click="isUpdateMode = false">
            Cancel
          </Button>
          <Button v-if="isUpdateMode && UserStore.appDelete" variant="destructive" @click="openDeleteDialog"
            :disabled="!UserStore.appDelete">
            Delete
          </Button>
          <Button @click="handleUpdateNote(selectedApp.id)" :disabled="!UserStore.appEdit">
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <!-- 업로드 실패시 알림창 -->
    <Dialog v-model:open="isApkErrorDialogOpen">
      <DialogContent class="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>업로드 실패</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div class="py-4 space-y-4 text-foreground ">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class=" font-medium">{{ ApkUploadError }}</p>
            </div>
          </div>
        </div>
        <DialogFooter class="gap-2">
          <Button variant="outline" @click="isApkErrorDialogOpen = false">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>

  <!-- 앱 삭제 확인창 -->
  <Dialog v-model:open="isDeleteDialogOpen" @update:open="closeDeleteDialog">
    <form>
      <!--릴리즈에서 사용중인 앱이 있을 경우-->
      <DialogContent v-if="inuseReleaseList.length > 0"
        class="sm:max-w-[500px] sm:max-h-[90%] sm:max-h-[90%] overflow-auto">
        <DialogHeader>
          <DialogTitle>
            <div class="flex items-center gap-2">
              <AlertTriangle class="w-6 h-6 text-red-500" />
              앱 삭제
            </div>
          </DialogTitle>
          <hr class="my-2 bg-" />
          <DialogDescription style="white-space: pre-line;">
            선택한 앱은 현재 사용 중인 릴리즈가 존재하여 삭제가 불가능합니다. <br /><br />
            사용중인 릴리즈 목록<br />
            <div class="border rounded-lg bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead class="px-5 w-20">ID</TableHead>
                    <TableHead >Title</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow v-for="release in inuseReleaseList" :key="release.id">
                    <TableCell class="px-5 w-20">{{ release.id }}</TableCell>
                    <TableCell class="max-w-[0] truncate">{{ release.title }}</TableCell>                  
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="closeDeleteDialog" class="w-[80px]">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>

      <!--릴리즈에서 사용중인 앱이 없을 경우-->
      <DialogContent v-else class="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <div class="flex items-center gap-2">
              <AlertTriangle class="w-6 h-6 text-red-500" />
              앱 삭제
            </div>
          </DialogTitle>
          <hr class="my-2 bg-" />
          <DialogDescription style="white-space: pre-line;">
            <div v-if="selectedApp" class="my-4 rounded-md border bg-muted/40 p-4 space-y-3 text-sm">
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Chipset</span>
                  <span class="text-muted-foreground font-medium">
                    {{RSStore.chipsetList.find(c => c.id === selectedApp.chipset_id)?.name || '-'}}
                  </span>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Type</span>
                  <span class="text-muted-foreground font-medium">
                    {{RSStore.pkgtypeList.find(pt => pt.id === RSStore.pkgList.find(p => p.id ===
                selectedApp.package_id)?.pkgtype_id)?.name}}
                  </span>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Package name</span>
                  <span class="text-muted-foreground font-medium">
                    {{ RSStore.pkgList.find(p => p.id === selectedApp.package_id)?.pkgname || '-' }}
                  </span>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Version Name</span>
                  <span class="text-muted-foreground font-medium">
                    {{ selectedApp.vername || '-' }}
                  </span>
                </div>
                <div class="grid grid-cols-[130px_1fr] items-center gap-2">
                  <span class="text-foreground font-semibold">Version Code</span>
                  <span class="text-muted-foreground font-medium">
                    {{selectedApp.vercode || '-'}}
                  </span>
                </div>
              </div>
              <p class="text-muted-foreground text-red-500">
                앱을 삭제하시겠습니까?
              </p>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="isDeleteDialogOpen = false" class="w-[80px]">
            Cancel
          </Button>
          <template v-if="!isSpinner">
            <Button @click="handleDelete" :disabled="isSpinner" class="w-[80px]">
              Confirm
            </Button>
          </template>
          <template v-else>
            <Button class="w-[80px]" disabled>
              <Spinner class="mx-auto h-8 w-8" />
            </Button>
          </template>
        </DialogFooter>
      </DialogContent>
    </form>
  </Dialog>

</template>


<style scoped>
/* 에디터 컨테이너 클래스 내부의 변수를 수정 */
:deep(.jse-main) {
  /* 메뉴바 전체 배경색 */
  --jse-theme-color: #000000;
  --jse-theme-color-highlight: #333333;
}
</style>